import { useState } from 'react';
import { personalizedCategories, type Category } from '../../../mocks/categories';
import { openWhatsApp } from '../../../utils/whatsapp';
import { trackEvent } from '../../../utils/tracking';
import InlineCitySelector from './InlineCitySelector';

const profileTags = ['Kadın', '25–34 yaş', 'Şehirli', 'Aktif kullanıcı'];

interface PersonalizedCardProps {
  cat: Category;
}

function PersonalizedCard({ cat }: PersonalizedCardProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSend = (city: string) => {
    trackEvent('category_click', { category: cat.name, city, section: 'personalized' });
    trackEvent('whatsapp_redirect', { category: cat.name, city });
    openWhatsApp(cat.name, city);
  };

  return (
    <div
      className="group relative rounded-2xl transition-all duration-300"
      style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${cat.gradientFrom}30` }}
    >
      {/* Personalized badge */}
      <div
        className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full font-medium z-10"
        style={{ background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.3)' }}
      >
        Sana Özel
      </div>

      {/* Clickable main area */}
      <div
        className="p-5 cursor-pointer"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
        aria-label={`${cat.name} için şehir seç`}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
          style={{ background: `${cat.gradientFrom}20`, border: `1px solid ${cat.gradientFrom}35` }}
        >
          <i className={`${cat.icon} text-xl`} style={{ color: cat.gradientFrom }} />
        </div>

        <h4
          className="text-white font-semibold text-sm mb-1"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {cat.name}
        </h4>
        <p className="text-gray-600 text-xs leading-relaxed mb-4">{cat.description}</p>

        <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: cat.gradientFrom }}>
          <i className="ri-map-pin-line" />
          Şehir Seç &amp; Kampanya Al
        </div>
      </div>

      {open && (
        <InlineCitySelector
          accentColor={cat.gradientFrom}
          targetName={cat.name}
          onClose={handleClose}
          onSend={handleSend}
        />
      )}
    </div>
  );
}

export default function PersonalizedSection() {
  return (
    <section className="bg-[#0a0a0f] py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(236,72,153,0.06), rgba(6,182,212,0.06))',
            border: '1px solid rgba(168,85,247,0.2)',
          }}
        >
          <div className="p-8 md:p-12">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 gap-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🎯</span>
                  <h2
                    className="text-white font-bold text-2xl md:text-3xl"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Sana Özel Kampanyalar
                  </h2>
                </div>
                <p className="text-gray-500 text-sm">Profiline göre özelleştirilmiş fırsat kategorileri</p>
              </div>

              <div className="flex flex-wrap gap-2">
                {profileTags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{
                      background: 'rgba(168,85,247,0.12)',
                      border: '1px solid rgba(168,85,247,0.25)',
                      color: '#c084fc',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {personalizedCategories.map((cat) => (
                <PersonalizedCard key={cat.id} cat={cat} />
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm">
                ✨ Bu kategoriler ziyaret geçmişinize göre önerilmiştir.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
